// ============================================================
// Oberflächenzustand — was NICHT vom Server kommt (SPEC §7).
//
// Die Grenze ist scharf und hat einen Grund: Serverdaten gehören TanStack
// Query (`api/hooks.ts`), Oberflächenzustand gehört hierher. Ein Projekt, das
// hier läge, wäre eine zweite Wahrheit neben `['stand']`; eine Auswahl, die
// in einer Abfrage läge, ginge bei jedem Neuholen verloren.
//
// ---- Die öffentliche Form, für F2/F3/F4 -----------------------------------
//
//   const auswahl     = useUi((s) => s.auswahl);          // {art, id} | null
//   const setAuswahl  = useUi((s) => s.setAuswahl);       // (a|null) => void
//   const waehle      = useUi((s) => s.waehle);           // (art, id) => void  — schaltet um
//   const filter      = useUi((s) => s.filter);
//   const setFilter   = useUi((s) => s.setFilter);        // Teilmenge reicht
//   const zeichnen    = useUi((s) => s.zeichnen);         // "aus"|"zeichnen"|"bearbeiten"
//   const setZeichnen = useUi((s) => s.setZeichnen);
//   const erfolg      = useUi((s) => s.erfolg);           // Feier (§16.3) | null
//   const feiern      = useUi((s) => s.feiern);           // (Feier|null) => void
//   const gelaende    = useUi((s) => s.gelaende);         // Geländeschalter (E18)
//   const gebaeude    = useUi((s) => s.gebaeude);         // 3D-Gebäude
//   const beitraege   = useUi((s) => s.beitraege);        // Beiträge mit Ort (§37.10)
//   const zeigen      = useUi((s) => s.ansichtZeigen);    // (ProjektAnsicht|null) => void (§37)
//   const farbe       = useUi((s) => s.bueroFarben[id]);  // Bürofarbe je Kennung
//
// IMMER MIT SELEKTOR LESEN. `useUi()` ohne Selektor zeichnet die Komponente
// bei jeder Bewegung des Filters neu — auf der Karte ist das der Unterschied
// zwischen flüssig und ruckelig.
//
// `bueroFarben` ist eine Ableitung aus `Stand.bueros` und liegt hier, damit
// <Avatar> den Ring seines Heimatbüros einfärben kann, ohne selbst `/stand`
// abzufragen: der Avatar steht auch auf Bildschirmen, die den Stand gar nicht
// laden dürfen (abgemeldet), und ein Haken dort löste genau dort einen 401
// aus. `Shell` (und die Karte) füllen die Tafel, sobald der Stand da ist.
// ============================================================
import { create } from "zustand";
import type { LonLat, ProjektAnsicht, ProjektArt, ProjektTeam } from "../api/types";
import { load, save } from "../lib/storage";

export type AuswahlArt = "projekt" | "person" | "buero";

export interface Auswahl {
  art: AuswahlArt;
  id: string;
}

/**
 * Der Gegenstand der Übersicht (§14.4): das Büro oder die Person, deren
 * Projekte im großen Blatt stehen. `null` heißt: keine Übersicht offen.
 *
 * Sie liegt HIER und nicht in der Karte, weil sie von zwei Stellen aus
 * aufgeht — von der Bürokarte und von der Personenkarte — und weil ein Blatt,
 * das über allem liegt, keinen Prop-Weg durch drei Ebenen nehmen sollte.
 * Gespeichert wird nichts: eine Übersicht ist ein Blick, kein Zustand, den
 * man beim nächsten Besuch wiederfinden will.
 */
export interface Uebersicht {
  art: "buero" | "person";
  id: string;
}

/**
 * Die Filterleiste der Karte (E8) und der Ansichten. Eine LEERE Liste heißt
 * „alle" und nicht „keine" — das ist die Lesart der Entwurfsmappe, in der
 * „Alle Büros" ein eingeschalteter Chip neben drei ausgeschalteten ist.
 */
export interface Filter {
  /** Büro-Kennungen; leer = alle Büros */
  bueros: string[];
  /** Phasen-Kennungen; leer = alle Phasen */
  phasen: string[];
  /** Personen-Kennungen; leer = alle Personen */
  personen: string[];
  /** Projektarten; leer = alle Arten */
  arten: ProjektArt[];
  /** Teams des Hauses (007); leer = alle Teams — Projekte OHNE Team fallen
   *  heraus, sobald hier etwas steht: „Wettbewerb" heisst Wettbewerb, nicht
   *  „Wettbewerb und alles Unsortierte". */
  teams: ProjektTeam[];
  /** nur Projekte, in deren Team ich stehe */
  meine: boolean;
  /** nur Projekte mit einer Abgabe in den nächsten sieben Tagen */
  baldFaellig: boolean;
  /** nur Projekte mit gesetztem Öffentlich-Kennzeichen */
  nurOeffentlich: boolean;
  /** Freitext aus dem Suchfeld der Kopfleiste */
  suche: string;
}

export const LEERER_FILTER: Filter = {
  bueros: [],
  phasen: [],
  personen: [],
  arten: [],
  teams: [],
  meine: false,
  baldFaellig: false,
  nurOeffentlich: false,
  suche: "",
};

/** Zeichenmodus der Projektgrenze (E13). `bearbeiten` schiebt vorhandene Punkte. */
export type Zeichenmodus = "aus" | "zeichnen" | "bearbeiten";

/**
 * Ein angetipptes Steckbriefwort (SPEC §10.2). Solange eines gesetzt ist,
 * stehen auf der Karte nur noch die Avatare derer hell, die dasselbe Wort
 * führen — „wer macht das auch?" in EINER Handlung, ohne einen Graphen.
 *
 * Die Art gehört dazu und nicht nur das Wort: „Englisch" steht bei manchen
 * unter Fähigkeiten und bei anderen unter Sprachen, und ein Chip, der aus der
 * einen Reihe die andere mitleuchten ließe, erklärte die Gruppen für Zierrat.
 */
export type SteckbriefArt = "faehigkeit" | "software" | "sprache";
export interface Hervorhebung {
  art: SteckbriefArt;
  wort: string;
}

/**
 * Ein Sprungziel für die Karte — gesetzt von der Suche, verbraucht von
 * `Karte.tsx`.
 *
 * Warum über den Speicher und nicht als Requisite: das Suchfeld sitzt in der
 * Kopfleiste, die Karte liegt in einem ganz anderen Zweig des Baumes, und
 * zwischen beiden liegt ein `React.lazy`. Eine Requisite durch diese Grenze zu
 * reichen ginge, wäre aber genau die Kopplung, die später jemand auflöst,
 * indem er eine zweite Karte baut (dieselbe Überlegung wie beim `KarteKontext`).
 *
 * Die Karte RÄUMT das Ziel weg, sobald sie geflogen ist. Bliebe es stehen,
 * spränge sie beim nächsten Anstrich noch einmal dorthin.
 */
export interface Sprung {
  punkt: LonLat;
  /** Der ZIELzoom — der Erzeuger entscheidet. Ein Büro will die Stadt (12),
   *  ein Ort das Grundstück (15). Kein Mindestwert: wer bei 17 steht und
   *  „Hamburg" wählt, soll die Stadt sehen, nicht ein Dach. */
  zoom: number;
  /** Neigung/Richtung eines Bueros mit gespeicherter Ansicht (033) — fehlen sie,
   *  fliegt die Karte flach (0/0) hin, wie bisher. */
  neigung?: number;
  richtung?: number;
}

/**
 * Die Feier (§16.3) — das Konfetti-Blatt über allem.
 *
 * Sie liegt hier und nicht bei „Aktuelles", weil sie AUSGELÖST wird, wo der
 * Erfolg entsteht (eine abgegebene Abgabe, ein neues Projekt) und GEZEIGT
 * wird, wo die Schale steht. Zwischen beiden liegen drei Ebenen Requisiten;
 * ein Feld im Oberflächenspeicher ist der kürzere Weg.
 *
 * `art` gehört dazu und nicht nur der Titel: die kleine Zeile darüber
 * („Abgabe abgegeben" / „Neues Projekt") muss übersetzbar sein, und
 * `api/hooks.ts`, das die Feier auslöst, hat kein Wörterbuch.
 */
export interface Feier {
  /** `abzeichen` (§32.6): ein privates Abzeichen, neu nach einer EIGENEN
   *  Handlung — nur auf Spielstufe „viel", ausgelöst von
   *  `screens/start/spiel/useAbzeichenFeier.ts`. */
  art: "abgabe" | "projekt" | "wettbewerb" | "abzeichen";
  titel: string;
  untertitel?: string;
}

const K_GELAENDE = "karte.gelaende";
// Neuer Schluessel seit 18.09.: die Vorgabe wechselte von „3D an" auf „flach".
// Unter dem alten Namen laege bei jedem, der die Karte je geoeffnet hat, ein
// gespeichertes `true` — die neue Vorgabe kaeme dort nie an.
const K_GEBAEUDE = "karte.dreid";
const K_LUFTBILD = "karte.luftbild";
// §37.10: die Ebene „Beiträge mit Ort“. AN als Vorgabe — wer die Sprechblasen
// nicht will, schaltet sie einmal aus, und das bleibt.
const K_BEITRAEGE = "karte.beitraege";

/**
 * Die zweite Angabe der vier Kartenschalter (§37.8). `merken: false` setzt den
 * Schalter NUR für diese Sitzung und lässt die gespeicherte Vorliebe stehen.
 *
 * Wofür: eine Projektansicht bringt ihre eigenen Schalter mit („3D an, Luftbild
 * an“). Wer sie aufruft, hat damit nicht gesagt, dass er die Karte künftig so
 * öffnen will — ohne diese Angabe überschriebe jede aufgerufene Ansicht die
 * Vorliebe des Geräts, und beim nächsten Besuch stünde die Karte im Luftbild
 * eines fremden Grundstücks. Ohne Angabe (der Knopf in der rechten Leiste)
 * wird gemerkt wie bisher.
 */
export interface SchalterOpt {
  /** Standard `true`: im Gerät speichern (`lp.karte.*`). `false`: nur jetzt. */
  merken?: boolean;
}

interface UiZustand {
  auswahl: Auswahl | null;
  setAuswahl(a: Auswahl | null): void;
  /** Dasselbe noch einmal antippen hebt die Auswahl auf — so ist es in der Mappe. */
  waehle(art: AuswahlArt, id: string): void;

  /** Offenes Übersichtsblatt (§14.4); `null` = keines. */
  uebersicht: Uebersicht | null;
  setUebersicht(u: Uebersicht | null): void;

  filter: Filter;
  setFilter(teil: Partial<Filter>): void;
  filterZuruecksetzen(): void;
  /** Eine Kennung in einer der fünf Listen an- oder abwählen. */
  filterUmschalten(feld: "bueros" | "phasen" | "personen" | "arten" | "teams", id: string): void;

  /** Angetipptes Steckbriefwort; `null` = niemand ist hervorgehoben. */
  hervorgehoben: Hervorhebung | null;
  setHervorgehoben(h: Hervorhebung | null): void;
  /** Dasselbe Wort noch einmal antippen löst die Hervorhebung wieder. */
  hervorhebenUmschalten(art: SteckbriefArt, wort: string): void;

  /** Sprungziel aus der Suche; die Karte fliegt hin und setzt es zurück. */
  sprung: Sprung | null;
  setSprung(z: Sprung | null): void;

  /**
   * Eine Projektansicht, die die Karte anwenden soll (§37.8) — derselbe Kanal
   * wie `sprung`: gesetzt von außen (Projekt-Overlay, Plus-Menü, Liste über
   * dem ⓘ, Tastenkürzel), verbraucht von `Karte.tsx`, die ein offenes Overlay
   * schließt, Schalter und Filter setzt, fliegt und den Wunsch mit
   * `ansichtZeigen(null)` wegräumt. Bliebe er stehen, flöge sie beim nächsten
   * Anstrich noch einmal hin.
   *
   * Nur wo die Karte EINGEHÄNGT ist. Die Startseite (Karte nicht im Baum)
   * verlinkt stattdessen auf `/karte?ansicht=<id>` (`lib/wege.ts`).
   */
  ansichtWunsch: ProjektAnsicht | null;
  ansichtZeigen(a: ProjektAnsicht | null): void;

  /** Laufende Feier (§16.3); `null` = keine. */
  erfolg: Feier | null;
  feiern(f: Feier | null): void;

  zeichnen: Zeichenmodus;
  setZeichnen(m: Zeichenmodus): void;

  gelaende: boolean;
  setGelaende(an: boolean, opt?: SchalterOpt): void;
  gebaeude: boolean;
  setGebaeude(an: boolean, opt?: SchalterOpt): void;
  /** Luftbild (Orthophotos der Länder + basemap.at, SPEC §13) unter der Basiskarte. */
  luftbild: boolean;
  setLuftbild(an: boolean, opt?: SchalterOpt): void;
  /** Beiträge mit Ort als Sprechblasen auf der Karte (§37.10), Schalter in der rechten Leiste. */
  beitraege: boolean;
  setBeitraege(an: boolean, opt?: SchalterOpt): void;
  /** Stadtgrenzen (§30) als Linie ueber der Karte — vorerst Wiesbaden. */
  stadtgrenzen: boolean;

  bueroFarben: Record<string, string>;
  setBueroFarben(tafel: Record<string, string>): void;
}

export const useUi = create<UiZustand>((set, get) => ({
  auswahl: null,
  setAuswahl(a) {
    // Die Hervorhebung hängt an der Personenkarte, auf der der Chip steht:
    // fällt die Auswahl weg, ist auch der Chip weg — bliebe die Hervorhebung
    // stehen, sähe die Karte halb erloschen aus, ohne dass noch etwas
    // sichtbar wäre, das sie erklärt.
    set(a === null ? { auswahl: null, hervorgehoben: null } : { auswahl: a });
  },
  waehle(art, id) {
    const jetzt = get().auswahl;
    const neu = jetzt && jetzt.art === art && jetzt.id === id ? null : { art, id };
    set(neu === null ? { auswahl: null, hervorgehoben: null } : { auswahl: neu, hervorgehoben: null });
  },

  uebersicht: null,
  setUebersicht(u) {
    set({ uebersicht: u });
  },

  filter: LEERER_FILTER,
  setFilter(teil) {
    set((s) => ({ filter: { ...s.filter, ...teil } }));
  },
  filterZuruecksetzen() {
    set({ filter: LEERER_FILTER });
  },
  filterUmschalten(feld, id) {
    set((s) => {
      // `arten` und `teams` sind enger typisiert als die drei Kennungslisten
      // (`ProjektArt[]` / `ProjektTeam[]`, nicht `string[]`), darum je ein
      // eigener Zweig statt eines berechneten Schlüssels über alle fünf: die
      // Aufrufer kommen aus `PROJEKT_ARTEN` bzw. `PROJEKT_TEAMS`, und die
      // Umwandlung steht hier EINMAL statt an jeder Chipreihe.
      if (feld === "arten") {
        const art = id as ProjektArt;
        const alt = s.filter.arten;
        const neu = alt.includes(art) ? alt.filter((x) => x !== art) : [...alt, art];
        return { filter: { ...s.filter, arten: neu } };
      }
      if (feld === "teams") {
        const team = id as ProjektTeam;
        const alt = s.filter.teams;
        const neu = alt.includes(team) ? alt.filter((x) => x !== team) : [...alt, team];
        return { filter: { ...s.filter, teams: neu } };
      }
      const alt = s.filter[feld];
      const neu = alt.includes(id) ? alt.filter((x) => x !== id) : [...alt, id];
      return { filter: { ...s.filter, [feld]: neu } };
    });
  },

  hervorgehoben: null,
  setHervorgehoben(h) {
    set({ hervorgehoben: h });
  },
  hervorhebenUmschalten(art, wort) {
    const jetzt = get().hervorgehoben;
    set({ hervorgehoben: jetzt && jetzt.art === art && jetzt.wort === wort ? null : { art, wort } });
  },

  sprung: null,
  setSprung(z) {
    set({ sprung: z });
  },

  ansichtWunsch: null,
  ansichtZeigen(a) {
    set({ ansichtWunsch: a });
  },

  erfolg: null,
  feiern(f) {
    set({ erfolg: f });
  },

  zeichnen: "aus",
  setZeichnen(m) {
    set({ zeichnen: m });
  },

  // Gelände kostet auf dem Telefon spürbar Leistung (E18/T6) und ist deshalb
  // eine Wahl, die bleibt — nicht eine, die man bei jedem Besuch neu trifft.
  gelaende: load<boolean>(K_GELAENDE, true),
  setGelaende(an, opt) {
    if (opt?.merken !== false) save(K_GELAENDE, an);
    set({ gelaende: an });
  },
  // Aus als Vorgabe: die Karte oeffnet flach und ganz (alle Bueros im Bild);
  // wer den gekippten Blick mit Gebaeuden will, schaltet den Wuerfel ein.
  gebaeude: load<boolean>(K_GEBAEUDE, false),
  setGebaeude(an, opt) {
    if (opt?.merken !== false) save(K_GEBAEUDE, an);
    set({ gebaeude: an });
  },
  // Aus als Vorgabe: das Luftbild ist ein Werkzeug fuer den Blick auf EIN
  // Grundstueck, nicht die Ansicht, in der das Haus seine Karte liest.
  luftbild: load<boolean>(K_LUFTBILD, false),
  setLuftbild(an, opt) {
    if (opt?.merken !== false) save(K_LUFTBILD, an);
    set({ luftbild: an });
  },
  beitraege: load<boolean>(K_BEITRAEGE, true),
  setBeitraege(an, opt) {
    if (opt?.merken !== false) save(K_BEITRAEGE, an);
    set({ beitraege: an });
  },
  // AN als Vorgabe — anders als die Blicke oben: eine Grenze ist Lesehilfe,
  // kein Werkzeug. Sie kostet nichts (ein Strich), und wer sie nicht will,
  // schaltet sie einmal aus; das bleibt.
  // Stadtgrenzen (§30) stehen immer: der Schalter in der Kartenleiste ist am
  // 29.09.2026 auf Wunsch wieder entfallen. Ein früher gespeichertes „aus“
  // (Schlüssel karte.stadtgrenzen) wird darum nicht mehr gelesen — sonst hinge,
  // wer es einmal ausgeschaltet hat, ohne Knopf zum Wiedereinschalten fest.
  stadtgrenzen: true,

  bueroFarben: {},
  setBueroFarben(tafel) {
    // Nur schreiben, wenn sich wirklich etwas geändert hat: `Shell` ruft das
    // nach JEDEM Neuholen von `/stand` (alle 60 s), und ein neues Objekt
    // zeichnete jeden Avatar der Anwendung neu.
    const alt = get().bueroFarben;
    const kAlt = Object.keys(alt);
    const kNeu = Object.keys(tafel);
    if (kAlt.length === kNeu.length && kNeu.every((k) => alt[k] === tafel[k])) return;
    set({ bueroFarben: tafel });
  },
}));
